import { useCallback, useEffect, useState } from 'react';
import { getActiveTenantCode } from '../lib/firebase';

// First-time agent onboarding walkthrough. Self-shows once per device on first
// entry (localStorage flag), reopenable via the floating "?" button or a
// `chatolam:open-guide` window event (e.g. from a menu).
//
// Tenant gating (Phase 3): bekesher — and the default/unset case — keeps the
// original Hebrew/RTL Bekesher-branded walkthrough exactly as it was; any
// other tenant gets a compact generic English (LTR) rendition of the SAME
// structural component, with no Bekesher URLs/names and the bot referred to
// generically. The active tenant is the agent's stored selection (localStorage
// 'chatolam.selectedTenant') falling back to the build-time tenant
// (VITE_FIRESTORE_DATABASE_ID, then VITE_TENANT_CODE). The mock inbox inside
// stays LTR in both variants to match the real (English) agent app.

const SEEN_KEY = 'chatolam:agentGuideSeen';

const TENANT_CODE = getActiveTenantCode() || import.meta.env.VITE_TENANT_CODE || '';
const IS_BEKESHER = !TENANT_CODE || TENANT_CODE === 'bekesher';

type Step = {
  tag: string; of: string; title: string; body: string; tip: string; tk: string;
  focus: string; warn?: boolean; done?: boolean;
};

const STEPS: Step[] = [
  { tag: 'כניסה', of: 'שלב ראשון', title: 'כניסה למערכת', focus: 'top', tk: 'i',
    body: "היכנסו לכתובת <span class='ag-chip' dir='ltr'>cs.bekesher.net</span> ולחצו על <b>“Sign in with Google”</b>. התחברו עם חשבון ה‑<b>@bekesher.net</b> שלכם — זהו. ההרשאות שלכם כבר מוגדרות.",
    tip: 'אם אתם מחוברים לכמה חשבונות Google, בחרו את זה שמסתיים ב‑bekesher.net.' },
  { tag: 'מבנה המסך', of: 'שלב 2', title: 'שלושה אזורים במסך', focus: 'thread', tk: 'i',
    body: 'מימין לשמאל: <b>רשימת השיחות</b>, <b>השיחה עצמה</b> באמצע, ו<b>פרטי הלקוח</b> בצד. כל העבודה שלכם קורית כאן — אין צורך בשום דבר אחר.',
    tip: 'המסך באנגלית, אבל השיחות עם הלקוחות בעברית — זה תקין.' },
  { tag: 'לשוניות', of: 'שלב 3', title: 'הלשוניות למעלה', focus: 'tabs', tk: 'i',
    body: '<b>“New (AI)”</b> — שיחות חדשות שהבוט מטפל בהן. <b>“Assigned to me”</b> — מה שהוקצה אליכם. <b>“Resolved”</b> — שיחות שנסגרו. המספר על הלשונית = כמה ממתינות.',
    tip: 'התחילו כל משמרת מ‑“Assigned to me”, ואז עברו על “New (AI)”.' },
  { tag: 'הבוט בקי', of: 'שלב 4', title: 'בקי עונה אוטומטית', focus: 'ai', tk: '★',
    body: '<b>בקי 🤖</b> — עוזרת ה‑AI — עונה לרוב השיחות לבד, מסביב לשעון. <b>לא צריך לענות לכל הודעה.</b> תתמקדו במקומות שבהם באמת צריך אתכם.',
    tip: 'בקי משתמשת במאגר הידע של Bekesher. אם היא ענתה נכון — אין מה להוסיף.' },
  { tag: 'מצב רוח', of: 'שלב 5', title: 'עקבו אחרי מצב הרוח', focus: 'face', tk: '◉',
    body: "לכל שיחה יש חיווי <b>מצב רוח</b> של הלקוח: <b style='color:var(--ag-pos)'>ירוק</b> = מרוצה, <b style='color:var(--ag-warn)'>כתום</b> = ביניים, <b style='color:var(--ag-neg)'>אדום</b> = מתוסכל. אדום = הזמן להתערב.",
    tip: 'החיווי מתעדכן אחרי כל הודעה — גם כשאתם כבר בשיחה.' },
  { tag: 'התערבות', of: 'שלב 6', title: 'מתי לקחת שיחה', focus: 'list', tk: 'i',
    body: 'פתחו שיחה וקחו אותה כשהלקוח <b>מתוסכל</b>, <b>מבקש נציג אנושי</b>, או כשהבוט <b>העביר אליכם</b> (escalation). לחיצה על השיחה פותחת אותה — ומרגע שאתם עונים, היא שלכם.',
    tip: 'אפשר גם לשייך שיחה לעצמכם דרך “Assign to me” בראש השיחה.' },
  { tag: 'מענה', of: 'שלב 7', title: 'לענות ללקוח', focus: 'reply', tk: 'i',
    body: 'כתבו בתיבת התשובה למטה ולחצו <b>“Reply”</b> (או Enter). עברית מלאה נתמכת, כולל כיווניות RTL. ההודעה נשלחת ללקוח מיד — בערוץ שממנו פנה (אתר / וואטסאפ).',
    tip: 'Shift+Enter = שורה חדשה בלי לשלוח.' },
  { tag: 'תשובות מוכנות', of: 'שלב 8', title: 'תשובות מוכנות (/)', focus: 'canned', tk: 'i',
    body: 'הקלידו <b>“/”</b> בתיבת התשובה כדי לבחור <b>תשובה מוכנה</b> — טקסטים שמורים לשאלות נפוצות. בחרו, ערכו אם צריך, ושלחו. חוסך המון זמן.',
    tip: 'אתם יכולים להשתמש בכל התשובות המוכנות. עריכה/יצירה שלהן — לצוות הניהול בלבד.' },
  { tag: 'סגירה', of: 'שלב 9', title: 'סיום שיחה', focus: 'resolve', tk: '✓',
    body: 'סיימתם לעזור? לחצו <b>“Resolve”</b> כדי לסגור את השיחה. אם הלקוח יכתוב שוב — היא נפתחת מחדש אוטומטית, כך שלא מפספסים כלום.',
    tip: 'עדיף לסגור מאשר להשאיר פתוח — זה מנקה את התור לכולם.' },
  { tag: 'היסטוריה', of: 'שלב 10', title: 'היסטוריית הלקוח', focus: 'history', tk: 'i',
    body: 'שיחות קודמות של אותו לקוח <b>נערמות מעל</b> השיחה הנוכחית. לחצו על כרטיס כדי לפתוח שיחה ישנה ולראות מה כבר נאמר — בלי לצאת מהשיחה. בצד יש את <b>פרטי הלקוח</b>.',
    tip: 'תמיד הציצו בהיסטוריה לפני שעונים — חוסך ללקוח לחזור על עצמו.' },
  { tag: 'הרשאות', of: 'שלב 11', title: 'מה באחריותכם', focus: 'limits', tk: '⚙', warn: true,
    body: 'התפקיד שלכם הוא <b>לנהל שיחות</b>. הגדרות המערכת — מאגר ידע, אוטומציות, ערוצים, שעות פעילות — <b>נעולות ומנוהלות ע\"י הצוות הניהולי</b>. אין דרך שתשברו משהו בטעות.',
    tip: 'צריך לשנות תשובה מוכנה, שעות, או משהו בהגדרות? פנו למנהל/ת הצוות.' },
  { tag: 'מוכנים', of: 'סיום', title: 'זהו — אתם מוכנים! 🎉', focus: '', tk: '✓', done: true,
    body: 'קיבלתם את כל מה שצריך כדי להתחיל: להיכנס, לזהות שיחה שדורשת אתכם, לענות, ולסגור. שאר הזמן — בקי מכסה אתכם.',
    tip: 'אפשר לפתוח את המדריך שוב בכל רגע דרך כפתור ה‑“?” בפינה. בהצלחה!' },
];

// Generic English steps for every non-bekesher tenant: same structure, no
// brand URLs/domains, the bot is just "the assistant".
const STEPS_EN: Step[] = [
  { tag: 'Sign in', of: 'Step 1', title: 'Signing in', focus: 'top', tk: 'i',
    body: 'Open the support console and click <b>“Sign in with Google”</b> with your work account — that’s it. Your permissions are already set up.',
    tip: 'Signed in to several Google accounts? Pick your work one.' },
  { tag: 'Layout', of: 'Step 2', title: 'Three areas on screen', focus: 'thread', tk: 'i',
    body: 'Left to right: the <b>conversation list</b>, the <b>conversation itself</b> in the middle, and <b>customer details</b> on the side. All of your work happens here — nothing else is needed.',
    tip: 'Customers may write in any language — reply in theirs.' },
  { tag: 'Tabs', of: 'Step 3', title: 'The tabs up top', focus: 'tabs', tk: 'i',
    body: '<b>“New (AI)”</b> — fresh conversations the assistant is handling. <b>“Assigned to me”</b> — what’s yours. <b>“Resolved”</b> — closed conversations. The number on a tab = how many are waiting.',
    tip: 'Start each shift with “Assigned to me”, then sweep “New (AI)”.' },
  { tag: 'The assistant', of: 'Step 4', title: 'The assistant replies automatically', focus: 'ai', tk: '★',
    body: 'The <b>AI assistant 🤖</b> answers most conversations on its own, around the clock. <b>You don’t need to answer every message.</b> Focus where a human is actually needed.',
    tip: 'The assistant works from your brand’s knowledge base. If it answered correctly — there’s nothing to add.' },
  { tag: 'Mood', of: 'Step 5', title: 'Watch the mood indicator', focus: 'face', tk: '◉',
    body: "Every conversation shows the customer’s <b>mood</b>: <b style='color:var(--ag-pos)'>green</b> = happy, <b style='color:var(--ag-warn)'>orange</b> = in between, <b style='color:var(--ag-neg)'>red</b> = frustrated. Red = time to step in.",
    tip: 'The indicator updates after every message — including while you’re in the conversation.' },
  { tag: 'Taking over', of: 'Step 6', title: 'When to take a conversation', focus: 'list', tk: 'i',
    body: 'Open and take a conversation when the customer is <b>frustrated</b>, <b>asks for a human</b>, or the assistant <b>escalated it to you</b>. Clicking a conversation opens it — and once you reply, it’s yours.',
    tip: 'You can also assign one to yourself via “Assign to me” at the top of the thread.' },
  { tag: 'Replying', of: 'Step 7', title: 'Answering the customer', focus: 'reply', tk: 'i',
    body: 'Type in the reply box below and click <b>“Reply”</b> (or press Enter). The message reaches the customer immediately — on the channel they wrote from (web / WhatsApp).',
    tip: 'Shift+Enter = new line without sending.' },
  { tag: 'Canned replies', of: 'Step 8', title: 'Canned replies (/)', focus: 'canned', tk: 'i',
    body: 'Type <b>“/”</b> in the reply box to pick a <b>canned reply</b> — saved answers to common questions. Pick one, tweak it if needed, and send. Saves a lot of time.',
    tip: 'You can use every canned reply. Editing/creating them is for the admin team only.' },
  { tag: 'Resolving', of: 'Step 9', title: 'Closing a conversation', focus: 'resolve', tk: '✓',
    body: 'Done helping? Click <b>“Resolve”</b> to close the conversation. If the customer writes again it reopens automatically — nothing gets lost.',
    tip: 'Better to resolve than to leave open — it clears the queue for everyone.' },
  { tag: 'History', of: 'Step 10', title: 'The customer’s history', focus: 'history', tk: 'i',
    body: 'The same customer’s earlier conversations <b>stack above</b> the current one. Click a card to open an old conversation and see what was already said — without leaving the thread. <b>Customer details</b> are on the side.',
    tip: 'Always glance at the history before replying — it saves the customer repeating themselves.' },
  { tag: 'Permissions', of: 'Step 11', title: 'What you’re responsible for', focus: 'limits', tk: '⚙', warn: true,
    body: 'Your job is <b>handling conversations</b>. System settings — knowledge base, automations, channels, business hours — are <b>locked and managed by the admin team</b>. There’s no way to break anything by accident.',
    tip: 'Need a canned reply, hours, or a setting changed? Ask your team lead.' },
  { tag: 'Ready', of: 'Done', title: 'That’s it — you’re ready! 🎉', focus: '', tk: '✓', done: true,
    body: 'You have everything you need to start: sign in, spot the conversations that need you, reply, and resolve. The rest of the time — the assistant has you covered.',
    tip: 'You can reopen this guide any time via the “?” button in the corner. Good luck!' },
];

// Per-variant chrome + mock-inbox strings. The Hebrew values are the exact
// pre-parameterization literals, so the bekesher render is unchanged.
type Chrome = {
  dir: 'rtl' | 'ltr';
  fabLabel: string; fabTitle: string; dialogLabel: string; closeLabel: string;
  brandGlyph: string; brandName: string; brandSub: string;
  stepCount: (n: number) => string; ofCount: (n: number) => string; stepAria: (n: number) => string;
  nextLabel: string; finishLabel: string; prevLabel: string;
  mock: {
    lock: string; title: string; user: string;
    convs: Array<{ init: string; color: string; name: string; preview: string }>;
    hist: string; histTag: string;
    custWho: string; cust1: string; aiWho: string; ai1: string; cust2: string;
    canned: Array<{ sc: string; label: string }>;
    input: string; send: string; resolve: string; mood: string;
  };
};

const CHROME_HE: Chrome = {
  dir: 'rtl',
  fabLabel: 'מדריך למוקדן חדש', fabTitle: 'מדריך למוקדן', dialogLabel: 'מדריך למוקדן חדש', closeLabel: 'סגירה',
  brandGlyph: 'ב', brandName: "Bekesher · צ'אט תמיכה", brandSub: 'מדריך הפעלה למוקדן חדש',
  stepCount: (n) => `שלב ${n}`, ofCount: (n) => `מתוך ${n}`, stepAria: (n) => `שלב ${n}`,
  nextLabel: '‹ הבא', finishLabel: 'סגירה', prevLabel: 'הקודם ›',
  mock: {
    lock: '⚙︎ הגדרות — לצוות הניהול בלבד',
    title: 'ChatOlam · Inbox', user: 'omer@bekesher.net ⌄',
    convs: [
      { init: 'ד', color: '#0ea5e9', name: 'דוד כהן', preview: 'מתי מגיע ה‑eSIM?' },
      { init: 'ר', color: '#16a34a', name: 'רות לוי', preview: 'תודה רבה!' },
      { init: 'א', color: '#d97706', name: 'Anonymous', preview: 'אין לי קליטה…' },
    ],
    hist: 'Web · שיחה קודמת · 24 ביוני', histTag: 'resolved ›',
    custWho: 'Customer', cust1: 'מתי מגיע כרטיס ה‑eSIM שלי?',
    aiWho: 'בקי 🤖 · AI', ai1: 'ה‑eSIM נשלח למייל תוך דקות מרגע ההזמנה…',
    cust2: 'אבל לא קיבלתי כלום 😕',
    canned: [
      { sc: '/esim', label: 'שליחת eSIM מחדש…' },
      { sc: '/hours', label: 'שעות פעילות' },
      { sc: '/thanks', label: 'תודה שפנית…' },
    ],
    input: 'הקלד תשובה…  ( / לתשובות מוכנות )', send: 'Reply', resolve: 'Resolve ✓',
    mood: 'מצב רוח: ממתין → עכשיו',
  },
};

const CHROME_EN: Chrome = {
  dir: 'ltr',
  fabLabel: 'New-agent guide', fabTitle: 'Agent guide', dialogLabel: 'New-agent guide', closeLabel: 'Close',
  brandGlyph: '✦', brandName: 'Support Console', brandSub: 'New-agent walkthrough',
  stepCount: (n) => `Step ${n}`, ofCount: (n) => `of ${n}`, stepAria: (n) => `Step ${n}`,
  nextLabel: 'Next ›', finishLabel: 'Close', prevLabel: '‹ Back',
  mock: {
    lock: '⚙︎ Settings — admin team only',
    title: 'ChatOlam · Inbox', user: 'agent@example.com ⌄',
    convs: [
      { init: 'D', color: '#0ea5e9', name: 'Dana C.', preview: 'When will my eSIM arrive?' },
      { init: 'S', color: '#16a34a', name: 'Sam R.', preview: 'Thanks so much!' },
      { init: 'A', color: '#d97706', name: 'Anonymous', preview: 'I have no signal…' },
    ],
    hist: 'Web · previous conversation · Jun 24', histTag: 'resolved ›',
    custWho: 'Customer', cust1: 'When will my eSIM card arrive?',
    aiWho: 'Assistant 🤖 · AI', ai1: 'Your eSIM is emailed within minutes of ordering…',
    cust2: 'But I didn’t get anything 😕',
    canned: [
      { sc: '/esim', label: 'Resend the eSIM…' },
      { sc: '/hours', label: 'Business hours' },
      { sc: '/thanks', label: 'Thanks for reaching out…' },
    ],
    input: 'Type a reply…  ( / for canned replies )', send: 'Reply', resolve: 'Resolve ✓',
    mood: 'Mood: waiting → now',
  },
};

const ACTIVE_STEPS = IS_BEKESHER ? STEPS : STEPS_EN;
const L = IS_BEKESHER ? CHROME_HE : CHROME_EN;
// Keyboard: in RTL "forward" is ArrowLeft (as before); in LTR it's ArrowRight.
const FWD_KEY = L.dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
const BACK_KEY = L.dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft';

export function AgentGuide() {
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);

  const openGuide = useCallback(() => { setI(0); setOpen(true); }, []);
  const close = useCallback(() => {
    setOpen(false);
    try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* ignore */ }
  }, []);

  // First-run auto-open + external reopen trigger.
  useEffect(() => {
    try { if (localStorage.getItem(SEEN_KEY) !== '1') setOpen(true); } catch { /* ignore */ }
    window.addEventListener('chatolam:open-guide', openGuide);
    return () => window.removeEventListener('chatolam:open-guide', openGuide);
  }, [openGuide]);

  // Keyboard: Esc closes; direction-aware arrows (RTL: Left = forward).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      else if (e.key === FWD_KEY || e.key === 'PageDown') setI((v) => Math.min(ACTIVE_STEPS.length - 1, v + 1));
      else if (e.key === BACK_KEY || e.key === 'PageUp') setI((v) => Math.max(0, v - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  const N = ACTIVE_STEPS.length;
  const s = ACTIVE_STEPS[i];
  const rc = (r: string) => 'region' + (s.focus === r ? ' is-spot' : '');

  // Nav buttons: "forward" sits on the start side in RTL (as before) and on
  // the end side in LTR, so DOM order flips with the variant.
  const nextBtn = (
    <button type="button" className="ag-btn next" onClick={() => (i < N - 1 ? setI(i + 1) : close())}>
      {i === N - 1 ? L.finishLabel : L.nextLabel}
    </button>
  );
  const prevBtn = (
    <button type="button" className="ag-btn prev" disabled={i === 0} onClick={() => setI(Math.max(0, i - 1))}>
      {L.prevLabel}
    </button>
  );

  return (
    <>
      <style>{CSS}</style>
      {!open && (
        <button type="button" className="ag-fab" onClick={openGuide} aria-label={L.fabLabel} title={L.fabTitle}>?</button>
      )}
      {open && (
        <div className="ag-root" dir={L.dir} role="dialog" aria-modal="true" aria-label={L.dialogLabel}>
          <div className="ag-backdrop" onClick={close} />
          <div className="ag-wrap">
            <header className="ag-top">
              <div className="ag-brand">
                <div className="ag-logo">{L.brandGlyph}</div>
                <div>
                  <div className="ag-name">{L.brandName}</div>
                  <div className="ag-sub">{L.brandSub}</div>
                </div>
              </div>
              <button type="button" className="ag-x" onClick={close} aria-label={L.closeLabel}>✕</button>
            </header>

            <div className="ag-rail">
              <span className="ag-count">{L.stepCount(i + 1)}</span>
              <div className="ag-bar"><div className="ag-fill" style={{ width: `${((i + 1) / N) * 100}%` }} /></div>
              <span className="ag-count">{L.ofCount(N)}</span>
            </div>

            <main className="ag-card">
              <section className="ag-copy">
                <div className="ag-badge">
                  <div className={'ag-num' + (s.done ? ' done' : '')}>{s.done ? '✓' : i + 1}</div>
                  <div>
                    <div className="ag-stepof">{s.of}</div>
                    <div className="ag-tag">{s.tag}</div>
                  </div>
                </div>
                <h2 className="ag-title">{s.title}</h2>
                <p className="ag-body" dangerouslySetInnerHTML={{ __html: s.body }} />
                <div className={'ag-tip' + (s.warn ? ' warn' : '')}>
                  <span className="ag-tipic">{s.tk}</span>
                  <span dangerouslySetInnerHTML={{ __html: s.tip }} />
                </div>
              </section>

              <section className="ag-stage">
                <div className={'mock' + (s.focus ? ' dim' : '')} data-focus={s.focus}>
                  <div className="lock">{L.mock.lock}</div>
                  <div className={rc('top') + ' m-top'}><span className="m-dot" /><span className="m-title">{L.mock.title}</span><span className="m-user">{L.mock.user}</span></div>
                  <div className={rc('tabs') + ' m-tabs'}>
                    <span className="on">All</span><span className="badge" data-n="5">New (AI)</span><span>Assigned to me</span><span>Resolved</span>
                  </div>
                  <div className="m-body">
                    <div className={rc('list') + ' m-list'}>
                      {L.mock.convs.map((c, k) => (
                        <div key={k} className={'m-conv' + (k === 0 ? ' sel' : '')}><span className="m-av" style={{ background: c.color }}>{c.init}</span><div className="m-meta"><div className="n">{c.name}</div><div className="p">{c.preview}</div></div></div>
                      ))}
                    </div>
                    <div className={rc('thread') + ' m-thread'}>
                      <div className={rc('history') + ' m-hist'}><span>{L.mock.hist}</span><span className="rez">{L.mock.histTag}</span></div>
                      <div className="bub cust"><span className="who">{L.mock.custWho}</span>{L.mock.cust1}</div>
                      <div className={rc('ai') + ' bub ai'}><span className="who">{L.mock.aiWho}</span>{L.mock.ai1}</div>
                      <div className="bub cust">{L.mock.cust2}</div>
                    </div>
                  </div>
                  <div className="m-canned">
                    {L.mock.canned.map((c, k) => (
                      <div key={k} className={'ci' + (k === 0 ? ' on' : '')}><span className="sc">{c.sc}</span><span>{c.label}</span></div>
                    ))}
                  </div>
                  <div className={rc('reply') + ' m-reply'}>
                    <span className="m-input">{L.mock.input}</span>
                    <span className="m-send">{L.mock.send}</span>
                    <span className={rc('resolve') + ' m-resolve'}>{L.mock.resolve}</span>
                  </div>
                  <div className="m-reply" style={{ borderTop: 0, paddingTop: 0 }}>
                    <span className={rc('face') + ' m-face'}><span className="dotface" style={{ background: 'var(--ag-neg)' }} />{L.mock.mood}</span>
                  </div>
                </div>
              </section>
            </main>

            <nav className="ag-nav">
              {/* RTL: forward is on the LEFT (‹ הבא); back on the right (הקודם ›). LTR flips. */}
              {L.dir === 'rtl' ? nextBtn : prevBtn}
              <div className="ag-dots">
                {ACTIVE_STEPS.map((_, k) => (
                  <button key={k} type="button" className={k === i ? 'on' : ''} aria-label={L.stepAria(k + 1)} onClick={() => setI(k)} />
                ))}
              </div>
              {L.dir === 'rtl' ? prevBtn : nextBtn}
            </nav>
          </div>
        </div>
      )}
    </>
  );
}

const CSS = `
.ag-root{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;padding:clamp(12px,3vw,32px);
  --ag-card:#fff;--ag-ink:#0f1b2a;--ag-muted:#5a6b7c;--ag-faint:#8598aa;--ag-line:#dbe4ee;--ag-line-soft:#e8eef5;
  --ag-sky:#0ea5e9;--ag-sky-deep:#0369a1;--ag-sky-wash:#e6f4fd;--ag-pos:#16a34a;--ag-warn:#d97706;--ag-neg:#e11d48;--ag-resolve:#0f9d58;
  --ag-font:-apple-system,BlinkMacSystemFont,"Segoe UI","Noto Sans Hebrew","Arial Hebrew",Arial,sans-serif;
  font-family:var(--ag-font);color:var(--ag-ink);line-height:1.6;-webkit-font-smoothing:antialiased}
.ag-root *{box-sizing:border-box}
.ag-backdrop{position:absolute;inset:0;background:rgba(9,22,40,.55);backdrop-filter:blur(3px)}
.ag-wrap{position:relative;z-index:1;width:100%;max-width:1000px;max-height:94vh;overflow:auto;display:flex;flex-direction:column;gap:16px;
  background:linear-gradient(180deg,#f6f9fc,#eef2f7);border:1px solid var(--ag-line);border-radius:22px;padding:clamp(16px,2.4vw,26px);
  box-shadow:0 40px 90px -30px rgba(3,105,161,.5)}
.ag-top{display:flex;align-items:center;justify-content:space-between;gap:12px}
.ag-brand{display:flex;align-items:center;gap:11px}
.ag-logo{width:38px;height:38px;border-radius:11px;background:linear-gradient(145deg,var(--ag-sky),var(--ag-sky-deep));display:grid;place-items:center;color:#fff;font-weight:800;font-size:19px;box-shadow:0 6px 16px -6px rgba(3,105,161,.6)}
.ag-name{font-weight:800;font-size:16px;letter-spacing:-.01em}
.ag-sub{font-size:12px;color:var(--ag-muted);font-weight:600}
.ag-x{width:34px;height:34px;border-radius:10px;border:1px solid var(--ag-line);background:#fff;color:var(--ag-muted);font-size:14px;cursor:pointer;font-weight:700}
.ag-x:hover{background:#f4f8fb;color:var(--ag-ink)}
.ag-rail{display:flex;align-items:center;gap:10px}
.ag-bar{flex:1;height:6px;border-radius:999px;background:#dfe8f1;overflow:hidden}
.ag-fill{height:100%;background:linear-gradient(90deg,var(--ag-sky),var(--ag-sky-deep));border-radius:999px;transition:width .45s cubic-bezier(.4,0,.2,1)}
.ag-count{font-size:12px;font-weight:700;color:var(--ag-muted);font-variant-numeric:tabular-nums;white-space:nowrap}
.ag-card{background:var(--ag-card);border:1px solid var(--ag-line);border-radius:18px;overflow:hidden;display:grid;grid-template-columns:1.05fr 1fr;min-height:380px;box-shadow:0 1px 2px rgba(15,27,42,.05)}
@media(max-width:720px){.ag-card{grid-template-columns:1fr}}
.ag-copy{padding:clamp(18px,3vw,32px);display:flex;flex-direction:column;gap:14px}
.ag-badge{display:flex;align-items:center;gap:12px}
.ag-num{width:50px;height:50px;flex:0 0 auto;border-radius:14px;display:grid;place-items:center;font-size:23px;font-weight:800;color:#fff;font-variant-numeric:tabular-nums;background:linear-gradient(150deg,var(--ag-sky),var(--ag-sky-deep));box-shadow:0 10px 22px -10px rgba(3,105,161,.7)}
.ag-num.done{background:linear-gradient(150deg,var(--ag-pos),#0b7a41)}
.ag-stepof{font-size:11px;font-weight:800;letter-spacing:.1em;color:var(--ag-faint)}
.ag-tag{font-size:12.5px;font-weight:700;color:var(--ag-sky-deep)}
.ag-title{margin:0;font-size:clamp(20px,2.6vw,27px);line-height:1.2;letter-spacing:-.02em;text-wrap:balance}
.ag-body{margin:0;color:#33475b;font-size:15.5px;max-width:46ch}
.ag-body b{color:var(--ag-ink)}
.ag-chip{display:inline-flex;align-items:center;background:#f1f6fb;border:1px solid var(--ag-line);border-radius:8px;padding:1px 8px;font-size:13px;font-weight:700;color:var(--ag-sky-deep);white-space:nowrap}
.ag-tip{margin-top:auto;display:flex;gap:11px;align-items:flex-start;background:linear-gradient(180deg,#fbfdff,#f3f9ff);border:1px solid var(--ag-line-soft);border-radius:13px;padding:12px 14px;font-size:13.5px;color:#40566b}
.ag-tipic{flex:0 0 auto;width:23px;height:23px;border-radius:7px;display:grid;place-items:center;background:var(--ag-sky-wash);color:var(--ag-sky-deep);font-size:13px;font-weight:800}
.ag-tip.warn{background:linear-gradient(180deg,#fffbf4,#fff6e9);border-color:#f6e6c9}
.ag-tip.warn .ag-tipic{background:#fdecc8;color:var(--ag-warn)}
.ag-stage{background:radial-gradient(90% 80% at 100% 0,#eaf5fe 0,rgba(234,245,254,0) 60%),linear-gradient(160deg,#f3f8fc,#e7eef6);border-inline-start:1px solid var(--ag-line);padding:clamp(16px,2.4vw,26px);display:grid;place-items:center}
@media(max-width:720px){.ag-stage{border-inline-start:none;border-top:1px solid var(--ag-line)}}
.ag-root .mock{direction:ltr;width:100%;max-width:380px;background:#fff;border:1px solid #d9e2ec;border-radius:14px;box-shadow:0 24px 60px -24px rgba(3,105,161,.35);overflow:hidden;font-size:11px;position:relative}
.ag-root .mock.dim .region:not(.is-spot){opacity:.42;filter:saturate(.7)}
.ag-root .mock .region{transition:opacity .35s,transform .35s,box-shadow .35s}
.ag-root .region.is-spot{position:relative;z-index:3;outline:2.5px solid var(--ag-sky);outline-offset:2px;border-radius:8px;box-shadow:0 0 0 6px rgba(14,165,233,.14),0 18px 30px -14px rgba(3,105,161,.5)}
@media(prefers-reduced-motion:reduce){.ag-root *{transition:none!important}}
.ag-root .m-top{display:flex;align-items:center;gap:7px;padding:9px 11px;border-bottom:1px solid #eef2f7}
.ag-root .m-dot{width:9px;height:9px;border-radius:50%;background:#0ea5e9}
.ag-root .m-title{font-weight:800;color:#0f1b2a;font-size:12px}
.ag-root .m-user{margin-inline-start:auto;font-size:10px;color:#8598aa;font-weight:600}
.ag-root .m-tabs{display:flex;gap:6px;padding:8px 11px;border-bottom:1px solid #eef2f7;flex-wrap:wrap}
.ag-root .m-tabs span{font-size:10px;font-weight:700;color:#5a6b7c;padding:3px 8px;border-radius:999px;background:#f2f6fa}
.ag-root .m-tabs span.on{background:#0ea5e9;color:#fff}
.ag-root .m-tabs span.badge::after{content:attr(data-n);margin-inline-start:4px;background:rgba(255,255,255,.35);border-radius:999px;padding:0 4px}
.ag-root .m-body{display:grid;grid-template-columns:116px 1fr}
.ag-root .m-list{border-inline-end:1px solid #eef2f7;padding:6px}
.ag-root .m-conv{display:flex;gap:7px;padding:6px;border-radius:8px;margin-bottom:3px}
.ag-root .m-conv.sel{background:#eaf5fe}
.ag-root .m-av{width:22px;height:22px;border-radius:50%;flex:0 0 auto;display:grid;place-items:center;color:#fff;font-weight:800;font-size:10px}
.ag-root .m-meta{min-width:0}
.ag-root .m-meta .n{font-weight:800;color:#0f1b2a;font-size:10.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ag-root .m-meta .p{color:#8598aa;font-size:9.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ag-root .m-thread{padding:9px;display:flex;flex-direction:column;gap:7px;min-height:150px}
.ag-root .m-hist{border:1px dashed #cddae8;border-radius:8px;padding:5px 8px;font-size:9.5px;color:#5a6b7c;display:flex;justify-content:space-between;align-items:center;background:#f7fafd}
.ag-root .m-hist .rez{color:var(--ag-resolve);font-weight:800;text-transform:uppercase;font-size:8.5px}
.ag-root .bub{max-width:80%;padding:6px 9px;border-radius:11px;font-size:10.5px;line-height:1.45}
.ag-root .bub.cust{align-self:flex-start;background:#fff;border:1px solid #e4ebf2;color:#0f1b2a}
.ag-root .bub.ai{align-self:flex-end;background:#eef2ff;border:1px solid #dfe4fb;color:#3b3f7a}
.ag-root .bub .who{display:block;font-size:8.5px;font-weight:800;opacity:.6;margin-bottom:1px}
.ag-root .m-reply{border-top:1px solid #eef2f7;padding:8px 9px;display:flex;gap:7px;align-items:center}
.ag-root .m-input{flex:1;background:#f4f8fb;border:1px solid #e1e9f1;border-radius:8px;padding:6px 8px;color:#8598aa;font-size:10px}
.ag-root .m-send{background:#0ea5e9;color:#fff;border-radius:8px;padding:6px 9px;font-weight:800;font-size:10px}
.ag-root .m-resolve{border:1px solid #bfe6cf;color:var(--ag-resolve);background:#f0faf4;border-radius:8px;padding:6px 9px;font-weight:800;font-size:10px}
.ag-root .m-face{margin-inline-start:auto;display:flex;align-items:center;gap:5px;font-size:9px;color:#5a6b7c;font-weight:700}
.ag-root .dotface{width:14px;height:14px;border-radius:50%}
.ag-root .m-canned{position:absolute;bottom:44px;inset-inline-start:9px;background:#fff;border:1px solid #d9e2ec;border-radius:9px;box-shadow:0 12px 32px -12px rgba(15,27,42,.25);padding:5px;width:180px;display:none;z-index:5}
.ag-root .mock[data-focus="canned"] .m-canned{display:block}
.ag-root .m-canned .ci{display:flex;gap:6px;padding:4px 6px;border-radius:6px;font-size:9.5px}
.ag-root .m-canned .ci.on{background:#eaf5fe}
.ag-root .m-canned .sc{font-weight:800;color:var(--ag-sky-deep)}
.ag-root .lock{position:absolute;inset-inline-end:9px;top:9px;font-size:9px;font-weight:800;color:var(--ag-warn);background:#fff6e9;border:1px solid #f6e6c9;border-radius:999px;padding:2px 7px;display:none;z-index:6}
.ag-root .mock[data-focus="limits"] .lock{display:block}
.ag-nav{display:flex;align-items:center;gap:12px}
.ag-dots{display:flex;gap:7px;flex:1;flex-wrap:wrap;justify-content:center}
.ag-dots button{width:9px;height:9px;border-radius:50%;border:0;background:#c6d4e2;padding:0;cursor:pointer;transition:transform .2s,background .2s}
.ag-dots button.on{background:var(--ag-sky-deep);transform:scale(1.35)}
.ag-dots button:focus-visible{outline:2px solid var(--ag-sky);outline-offset:3px}
.ag-btn{border:0;cursor:pointer;font-family:inherit;font-weight:800;font-size:14px;border-radius:12px;padding:11px 20px;display:inline-flex;align-items:center;gap:8px;transition:transform .12s,box-shadow .2s,background .2s}
.ag-btn:active{transform:translateY(1px)}
.ag-btn:focus-visible{outline:3px solid rgba(14,165,233,.5);outline-offset:2px}
.ag-btn.next{background:linear-gradient(150deg,var(--ag-sky),var(--ag-sky-deep));color:#fff;box-shadow:0 12px 24px -12px rgba(3,105,161,.8)}
.ag-btn.next:hover{transform:translateY(-1px)}
.ag-btn.prev{background:#fff;color:var(--ag-ink);border:1px solid var(--ag-line)}
.ag-btn.prev:disabled{opacity:.4;cursor:default}
.ag-fab{position:fixed;bottom:18px;inset-inline-start:18px;z-index:2147482000;width:44px;height:44px;border-radius:50%;border:0;cursor:pointer;
  background:linear-gradient(150deg,var(--ag-sky,#0ea5e9),var(--ag-sky-deep,#0369a1));color:#fff;font-size:20px;font-weight:800;
  box-shadow:0 12px 26px -10px rgba(3,105,161,.8);display:grid;place-items:center}
.ag-fab:hover{transform:translateY(-2px)}
.ag-fab:focus-visible{outline:3px solid rgba(14,165,233,.5);outline-offset:3px}
`;
